import { Flow, SignupData, SignupResponseSuccess } from '../types'
import { getLogger } from '@blueberry/observability'
import { poll } from '@blueberry/utils/common'

const logger = getLogger('signup/analytics/northbeam')

type NorthbeamPurchaseEvent = {
  id: string
  totalPrice: number
  shippingPrice: number
  taxPrice: number
  currency: 'USD'
  customerId: string
  lineItems: {
    productId: string
    variantId: string
    productName: string
    variantName: string
    price: number
    quantity: number
  }[]
}

declare global {
  interface Window {
    Northbeam: {
      identify: (key: string, value: string) => void
      trackPageView: (pageName: string) => void
      firePurchaseEvent: (event: NorthbeamPurchaseEvent) => void
    }
  }
}

export const northbeam = {
  async trackStep(flow: Flow, step: number, data: SignupData) {
    // we currently only use northbeam for d2c
    if (flow !== 'd2c') return

    const northbeam = await poll(() => window.Northbeam)
    if (!northbeam) {
      logger.error('Retries exhausted waiting for Northbeam to initialize')
      return
    }

    try {
      switch (step) {
        case 1:
          northbeam.trackPageView('Signup Page One')
          break
        case 2:
          northbeam.trackPageView('Signup Page Two')
          northbeam.identify('email', data.account?.email || '')
          break
        case 3:
          northbeam.trackPageView('Signup Page Three')
          break
      }
    } catch (error) {
      logger.error('Error tracking Northbeam step event:', error)
      return
    }
  },
  async trackSuccess(flow: Flow, response: SignupResponseSuccess) {
    // we currently only use northbeam for d2c
    if (flow !== 'd2c') return
    if (response.flow !== 'd2c') {
      throw new Error(
        `Incorrect response flow. Got ${response.flow}, expected d2c.`,
      )
    }

    const productId = response.monthsAtTime === 1 ? 'monthly_sku' : 'annual_sku'
    const variantId = response.monthsAtTime === 1 ? 'monthly' : 'annual'

    const northbeam = await poll(() => window.Northbeam)
    if (!northbeam) {
      logger.error('Retries exhausted waiting for Northbeam to initialize')
      return
    }

    northbeam.firePurchaseEvent({
      id: response.hashedSignupData + '_signup', // needs to match string in backend
      totalPrice: response.totalPrice,
      shippingPrice: 0,
      taxPrice: 0,
      currency: 'USD',
      customerId: response.hashedProfileId + '_customer', // needs to match string in backend
      lineItems: [
        {
          productId,
          variantId,
          productName: 'Signup',
          variantName: 'Small',
          price: response.totalPrice,
          quantity: 1,
        },
      ],
    })
  },
}
